import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  ShieldCheck,
  Loader2,
  ArrowRight,
  Calendar,
  Wallet,
} from "lucide-react";
import MarketingFooter from "../components/MarketingFooter";
import usePageMeta from "../hooks/usePageMeta";

function formatKES(value) {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-KE", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function VerifyMerchant() {
  const { handle } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  usePageMeta({
    title: `Verify ${handle} — Fortiqa`,
    description:
      "Verify a Fortiqa Trust Badge in real time. Confirms whether a merchant has active cyber cover.",
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/public/verify/${encodeURIComponent(handle)}`,
          { headers: { Accept: "application/json" } },
        );
        if (!res.ok) {
          throw new Error(
            res.status === 404
              ? "Unknown verification handle"
              : `Verification failed (${res.status})`,
          );
        }
        const data = await res.json();
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e.message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [handle]);

  const { loading, data, error } = state;
  const verified = data?.verified;

  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="verify-page">
      <header className="px-6 lg:px-10 max-w-5xl mx-auto pt-12 sm:pt-16 pb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#5c6360] hover:text-[#114232] mb-8"
        >
          ← Fortiqa
        </Link>
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#c15337] mb-2">
          Trust Badge · Verifier
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[0.95] text-[#114232]">
          {loading
            ? "Verifying…"
            : verified
              ? "Verified merchant"
              : data
                ? "Cover not active"
                : "Verification failed"}
        </h1>
      </header>

      <main className="px-6 lg:px-10 max-w-3xl mx-auto pb-16">
        {loading ? (
          <div
            className="rounded-3xl bg-white border border-[#114232]/10 p-12 grid place-items-center text-[#5c6360]"
            data-testid="verify-loading"
          >
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : error ? (
          <div
            className="rounded-3xl bg-white border border-[#c15337]/25 p-10 text-center"
            data-testid="verify-error"
          >
            <XCircle className="w-12 h-12 text-[#c15337] mx-auto" strokeWidth={1.5} />
            <div className="font-heading text-xl font-bold text-[#114232] mt-4">
              We couldn't verify this handle
            </div>
            <p className="text-sm text-[#5c6360] mt-2 leading-relaxed">
              {error}. If you typed the URL by hand, double-check the spelling — handles look
              like <span className="font-mono">business-name-abc12</span>.
            </p>
            <Link
              to="/trust-badge"
              className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-[#114232] hover:bg-[#0d3125] text-[#f4f2eb] text-sm font-semibold transition-colors"
            >
              About the Trust Badge <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div
            className={`rounded-3xl bg-white border p-7 sm:p-10 shadow-lg ${
              verified ? "border-[#2d7a4a]/30" : "border-[#d49a36]/30"
            }`}
            data-testid={verified ? "verify-success" : "verify-inactive"}
          >
            <div className="flex items-start gap-4">
              <img
                src="/fortiqa-mark.svg"
                alt="Fortiqa"
                className="w-16 h-16 object-contain shrink-0"
              />
              <div className="flex-1">
                <div className="font-heading text-2xl sm:text-3xl font-bold text-[#114232]">
                  {data.business_name}
                </div>
                <div className="text-xs text-[#5c6360] font-mono mt-1">
                  fortiqa.co.ke/verify/{data.handle}
                </div>
              </div>
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border ${
                  verified
                    ? "bg-[#2d7a4a]/10 text-[#2d7a4a] border-[#2d7a4a]/30"
                    : "bg-[#d49a36]/10 text-[#8a6520] border-[#d49a36]/30"
                }`}
              >
                {verified ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Live
                  </>
                ) : (
                  <>Inactive</>
                )}
              </span>
            </div>

            {verified ? (
              <div className="grid sm:grid-cols-3 gap-3 mt-7">
                <div className="rounded-xl bg-[#f4f2eb] border border-[#114232]/8 p-4">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#5c6360] font-bold">
                    <ShieldCheck className="w-3 h-3" /> Tier
                  </div>
                  <div className="font-heading font-bold text-lg text-[#114232] mt-1">
                    {data.tier}
                  </div>
                </div>
                <div className="rounded-xl bg-[#f4f2eb] border border-[#114232]/8 p-4">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#5c6360] font-bold">
                    <Wallet className="w-3 h-3" /> Cover limit
                  </div>
                  <div className="font-heading font-bold text-lg text-[#114232] mt-1 metric-value">
                    {formatKES(data.cover_limit_kes)}
                  </div>
                </div>
                <div className="rounded-xl bg-[#f4f2eb] border border-[#114232]/8 p-4">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#5c6360] font-bold">
                    <Calendar className="w-3 h-3" /> Active since
                  </div>
                  <div className="font-heading font-bold text-lg text-[#114232] mt-1">
                    {formatDate(data.active_since)}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-6 text-sm text-[#5c6360] leading-relaxed">
                This merchant doesn't currently have active Fortiqa cover. The badge
                auto-revokes when a policy lapses, so what you're seeing here is the truth.
              </div>
            )}

            <div className="mt-7 pt-6 border-t border-[#114232]/8 text-xs text-[#5c6360]">
              Last checked{" "}
              {data.checked_at
                ? new Date(data.checked_at).toLocaleString("en-KE", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })
                : "—"}
              {" · "}
              <Link to="/trust-badge" className="text-[#114232] hover:underline font-semibold">
                What is a Fortiqa Trust Badge?
              </Link>
            </div>
          </div>
        )}
      </main>

      <MarketingFooter />
    </div>
  );
}
